import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';

const isProduction = import.meta.env.PROD === true;

const EmployeeSignup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const invitedEmail = searchParams.get('email') || '';
  const hasInviteToken = Boolean(searchParams.get('token'));

  const [form, setForm] = useState({
    name: '',
    email: invitedEmail,
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');

    if (isProduction) {
      setError('Registrering er kun tilgjengelig via invitasjon.');
      return;
    }

    if (form.password.length < 8) {
      setError('Passordet må inneholde minst 8 tegn.');
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError('Passordene er ikke like.');
      return;
    }

    setLoading(true);

    const { error: signupError } = await supabase.auth.signUp({
      email: form.email.trim().toLowerCase(),
      password: form.password,
      options: {
        data: {
          full_name: form.name.trim()
        }
      }
    });

    setLoading(false);

    if (signupError) {
      setError(signupError.message);
      return;
    }

    setMessage('Registreringen er fullført. Du kan nå logge inn.');
    setTimeout(() => navigate('/login'), 1200);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg bg-white rounded-3xl border border-slate-200 shadow-xl p-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-xl bg-blue-600 text-white flex items-center justify-center">
            <SafeIcon icon="🛡️" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900">Registrer intern konto</h1>
            <p className="text-sm text-slate-500">
              {isProduction
                ? 'Registrering er kun tilgjengelig via invitasjon.'
                : 'Opprett tilgang til SalesWork'}
            </p>
          </div>
        </div>

        {isProduction && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-bold text-amber-800">
              Registrering er kun tilgjengelig via invitasjon.
            </p>
            <p className="mt-2 text-sm text-amber-700">
              Kontakt en administrator for å få tilgang.
            </p>
          </div>
        )}

        {!isProduction && (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-2">Fullt navn</label>
              <input
                required
                value={form.name}
                onChange={(event) => updateForm('name', event.target.value)}
                className="w-full p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ola Nordmann"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 mb-2">E-post</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(event) => updateForm('email', event.target.value)}
                className="w-full p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="jobb@bedrift.no"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 mb-2">Passord</label>
              <input
                required
                minLength="8"
                type="password"
                value={form.password}
                onChange={(event) => updateForm('password', event.target.value)}
                className="w-full p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 mb-2">
                Bekreft passord
              </label>
              <input
                required
                minLength="8"
                type="password"
                value={form.confirmPassword}
                onChange={(event) => updateForm('confirmPassword', event.target.value)}
                className="w-full p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {error && <p className="text-sm text-red-600 font-medium">{error}</p>}
            {message && <p className="text-sm text-emerald-600 font-medium">{message}</p>}

            <button
              disabled={loading}
              className="w-full py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 disabled:opacity-60"
            >
              {loading ? 'Registrerer...' : 'Opprett intern konto'}
            </button>
          </form>
        )}

        <button
          type="button"
          onClick={() => navigate('/login')}
          className="w-full mt-5 py-3 rounded-xl border border-slate-200 text-slate-700 font-bold hover:bg-slate-50"
        >
          Til innlogging
        </button>
      </div>
    </div>
  );
};

export default EmployeeSignup;